import { useState, useEffect } from "react";
import {
  allPieceTypes,
  nonKingablePieceTypes,
} from "../../services/zodiacEngine/const";
import { PieceType } from "../../services/zodiacEngine/types";
import styles from "./KingSelectionModal.module.css";
import ZodiacEngineService from "../ZodiacEngineService";
import { useTheme } from "../Providers/ThemeContext";

export interface KingSelectionModalProps {
  isOpen?: boolean; // Made optional with default true for backward compatibility
  isLight: boolean;
  playerType?: string; // Optional player type to display
  onKingSelected?: (king: PieceType, isLight: boolean) => void; // Optional callback
  showLookAwayWarning?: boolean; // Show "look away" warning for local PvP
}

export default function KingSelectionModal(props: KingSelectionModalProps) {
  const [isWaiting, setIsWaiting] = useState(false);
  const [selectedKing, setSelectedKing] = useState<PieceType | null>(null);
  const isOpen = props.isOpen !== undefined ? props.isOpen : true; // Default to true

  // DEBUG: Log state changes

  // CRITICAL FIX: Reset local state when switching between Light and Dark player
  // Without this, the "Bear Selected / Loading..." screen stays visible
  // when transitioning from Light selection to Dark selection
  useEffect(() => {
    setSelectedKing(null);
    setIsWaiting(false);
  }, [props.isLight]);

  function handleOnClick(piece: PieceType): void {
    // Show selection confirmation
    setSelectedKing(piece);

    // Delay before calling the callback
    setTimeout(() => {
      // If callback provided, use it (for Challenge AI mode)
      if (props.onKingSelected) {
        props.onKingSelected(piece, props.isLight);
      } else {
        // Otherwise, use the default behavior (for online PvP mode)
        ZodiacEngineService.zodiacEngine.setKing(piece, props.isLight);
        setIsWaiting(true);
      }
    }, 1200);
  }

  // duplicated code!!!
  function getPieces(isLight: boolean) {
    const suffix = isLight ? "light" : "dark";
    return (
      <div className={styles.pieceIcons}>
        {allPieceTypes
          .filter((piece) => !nonKingablePieceTypes.includes(piece) && piece !== PieceType.Virus)
          .map((piece) => (
            <div
              key={`${piece}-${suffix}`}
              className={styles.pieceIconContainer}
              onClick={(e) => handleOnClick(piece)}
            >
              <img
                src={`/icons/${piece}-${suffix}.png`}
                alt={`${piece}-${suffix}`}
                className={styles.pieceIcon}
              />
              <span className={styles.pieceName}>{piece}</span>
            </div>
          ))}
      </div>
    );
  }

  return (
    <div>
      {isOpen && (
        <div className={styles.modalOverlay}>
          <div
            className={styles.modalContainer}
            onClick={(e) => e.stopPropagation()}
          >
            {selectedKing ? (
              <div className={styles.selectionConfirmation}>
                <h2>{selectedKing} Selected</h2>
                <p>Loading game...</p>
              </div>
            ) : isWaiting ? (
              <div className={styles.waitingMessage}>
                <h2>Waiting for other player</h2>
              </div>
            ) : (
              <>
                <div className={styles.titleSection}>
                  <h2>
                    {props.playerType === 'Player (You)'
                      ? `${props.isLight ? 'Light' : 'Dark'} Player — Select King`
                      : props.playerType
                      ? `Select King for ${props.isLight ? 'Light' : 'Dark'}`
                      : 'Select Your King'}
                  </h2>
                  {props.showLookAwayWarning && (
                    <p className={styles.warningText}>
                      {props.isLight ? 'Dark player, please look away' : 'Light player, please look away'}
                    </p>
                  )}
                  <p className={styles.subtitle}>Choose which piece will be your king</p>
                </div>
                {getPieces(props.isLight)}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
